import React, { useState } from "react";
import { Search, Users, Link2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Badge, Button } from "../GraderUI";
import { CitationCompareModal } from "./CitationCompareModal";
import { ProviderStatusBadges } from "./ProviderStatusBadges";
import { ProviderIcon } from "../shared/ProviderIcon";
import {
	PROVIDERS,
	getQueryTypeClass,
	getQueryTypeLabel,
	preprocessResponse,
	queryMatchesFilter,
	getRowSources,
	formatGroundedSource,
	getRowSourceCount,
} from "./citationGridUtils";

const CONFIDENCE_CLASS = {
	high: "confidence--high",
	medium: "confidence--medium",
	low: "confidence--low",
	not_determined: "confidence--none",
};

const markdownComponents = {
	a: ({ href, children }) => (
		<a href={href} target="_blank" rel="noopener noreferrer">
			{children}
		</a>
	),
	strong: ({ children }) => <strong>{children}</strong>,
	em: ({ children }) => <em>{children}</em>,
	ul: ({ children }) => <ul>{children}</ul>,
	ol: ({ children }) => <ol>{children}</ol>,
	code: ({ children }) => <code>{children}</code>,
	blockquote: ({ children }) => <blockquote>{children}</blockquote>,
	p: ({ children }) => <p>{children}</p>,
};

export function CitationDataGrid({ results = [] }) {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeFilter, setActiveFilter] = useState("all");
	const [compareMode, setCompareMode] = useState(false);
	const [selectedRowDetail, setSelectedRowDetail] = useState(null);
	const [selectedCompareQuery, setSelectedCompareQuery] = useState(null);

	const handleCompareModeChange = (enabled) => {
		setCompareMode(enabled);
		setSelectedRowDetail(null);
		setSelectedCompareQuery(null);
	};

	const gridData = results.flatMap((result) => {
		const rows = [];

		if (result.openai_result) {
			rows.push({
				query: result.query,
				queryType: result.query_type,
				provider: "openai",
				providerName: "ChatGPT",
				...result.openai_result,
			});
		}

		if (result.gemini_result) {
			rows.push({
				query: result.query,
				queryType: result.query_type,
				provider: "gemini",
				providerName: "Google Gemini",
				...result.gemini_result,
			});
		}

		if (result.claude_result) {
			rows.push({
				query: result.query,
				queryType: result.query_type,
				provider: "claude",
				providerName: "Claude AI",
				...result.claude_result,
			});
		}

		return rows;
	});

	const filteredData = gridData.filter((row) => {
		if (searchQuery && !row.query.toLowerCase().includes(searchQuery.toLowerCase())) {
			return false;
		}
		if (activeFilter === "mentioned" && !row.mentioned) return false;
		if (activeFilter === "cited" && !row.cited) return false;
		if (activeFilter === "domain" && !row.domain_mentioned) return false;
		if (activeFilter === "brand" && !row.brand_mentioned) return false;
		return true;
	});

	const filteredCompareData = results.filter((result) => {
		if (searchQuery && !result.query.toLowerCase().includes(searchQuery.toLowerCase())) {
			return false;
		}
		if (activeFilter !== "all" && !queryMatchesFilter(result, activeFilter)) return false;
		return true;
	});

	const flatStats = {
		all: gridData.length,
		mentioned: gridData.filter((r) => r.mentioned).length,
		cited: gridData.filter((r) => r.cited).length,
		domain: gridData.filter((r) => r.domain_mentioned).length,
		brand: gridData.filter((r) => r.brand_mentioned).length,
	};

	const compareStats = {
		all: results.length,
		mentioned: results.filter((r) => queryMatchesFilter(r, "mentioned")).length,
		cited: results.filter((r) => queryMatchesFilter(r, "cited")).length,
		domain: results.filter((r) => queryMatchesFilter(r, "domain")).length,
		brand: results.filter((r) => queryMatchesFilter(r, "brand")).length,
	};

	const stats = compareMode ? compareStats : flatStats;

	const filters = [
		{ key: "all", label: "All" },
		{ key: "mentioned", label: "Mentioned" },
		{ key: "cited", label: "Cited" },
		{ key: "domain", label: "Domain" },
		{ key: "brand", label: "Brand" },
	];

	return (
		<div className="citation-data-grid">
			<div className="citation-data-grid__compare-bar">
				<label className="citation-data-grid__compare-toggle">
					<span className="citation-data-grid__compare-label">Compare mode</span>
					<input
						type="checkbox"
						className="citation-data-grid__compare-input"
						checked={compareMode}
						onChange={(e) => handleCompareModeChange(e.target.checked)}
					/>
					<span className="citation-data-grid__compare-switch" aria-hidden="true" />
				</label>
			</div>

			<div className="citation-data-grid__toolbar">
				<div className="citation-data-grid__search">
					<Search className="citation-data-grid__search-icon" />
					<input
						type="text"
						className="grader-input"
						placeholder="Filter by prompt"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>

				<div className="citation-data-grid__filters">
					{filters.map(({ key, label }) => (
						<Button
							key={key}
							variant={activeFilter === key ? "primary" : "outline"}
							className={activeFilter === key ? "grader-btn--filter-active" : ""}
							onClick={() => setActiveFilter(key)}
						>
							{label}
							<span className="citation-data-grid__filter-count">{stats[key]}</span>
						</Button>
					))}
				</div>
			</div>

			<div className="citation-data-grid__table-wrap">
				{compareMode ? (
					<table className="citation-data-grid__table citation-data-grid__table--compare">
						<thead className="citation-data-grid__thead">
							<tr>
								<th className="citation-data-grid__th">Prompt</th>
								<th className="citation-data-grid__th">Type</th>
								{PROVIDERS.map((provider) => (
									<th
										key={provider.key}
										className="citation-data-grid__th citation-data-grid__th--center"
									>
										<span className="citation-data-grid__provider-heading">
											<ProviderIcon provider={provider.key} size={18} />
											{provider.name}
										</span>
									</th>
								))}
								<th className="citation-data-grid__th citation-data-grid__th--center" />
							</tr>
						</thead>
						<tbody>
							{filteredCompareData.length === 0 ? (
								<tr>
									<td colSpan={6} className="citation-data-grid__empty">
										No results found
									</td>
								</tr>
							) : (
								filteredCompareData.map((result) => (
									<tr key={result.query} className="citation-data-grid__row">
										<td className="citation-data-grid__td citation-data-grid__td--prompt">
											<p
												className="citation-data-grid__prompt-text"
												title={result.query}
											>
												{result.query}
											</p>
										</td>
										<td className="citation-data-grid__td">
											<Badge className={getQueryTypeClass(result.query_type)}>
												{getQueryTypeLabel(result.query_type)}
											</Badge>
										</td>
										{PROVIDERS.map((provider) => (
											<td
												key={provider.key}
												className="citation-data-grid__td citation-data-grid__td--center citation-data-grid__td--provider-status"
											>
												<ProviderStatusBadges result={result[provider.resultKey]} />
											</td>
										))}
										<td className="citation-data-grid__td citation-data-grid__td--center">
											<button
												type="button"
												className="citation-data-grid__view-btn"
												onClick={() => setSelectedCompareQuery(result)}
											>
												View
											</button>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				) : (
					<table className="citation-data-grid__table">
						<thead className="citation-data-grid__thead">
							<tr>
								<th className="citation-data-grid__th">Prompt</th>
								<th className="citation-data-grid__th">Type</th>
								<th className="citation-data-grid__th">AI Model</th>
								<th className="citation-data-grid__th">Response</th>
								<th className="citation-data-grid__th citation-data-grid__th--center">Status</th>
								<th className="citation-data-grid__th citation-data-grid__th--center">Confidence</th>
								<th className="citation-data-grid__th citation-data-grid__th--center">Domain</th>
								<th className="citation-data-grid__th citation-data-grid__th--center">Brand</th>
								<th className="citation-data-grid__th citation-data-grid__th--center">Competitors</th>
								<th className="citation-data-grid__th citation-data-grid__th--center">Sources</th>
							</tr>
						</thead>
						<tbody>
							{filteredData.length === 0 ? (
								<tr>
									<td colSpan={10} className="citation-data-grid__empty">
										No results found
									</td>
								</tr>
							) : (
								filteredData.map((row, index) => (
									<tr
										key={index}
										className="citation-data-grid__row"
										onClick={() => setSelectedRowDetail(row)}
									>
										<td className="citation-data-grid__td citation-data-grid__td--prompt">
											<p className="citation-data-grid__prompt-text" title={row.query}>
												{row.query}
											</p>
										</td>
										<td className="citation-data-grid__td">
											<Badge className={getQueryTypeClass(row.queryType)}>
												{getQueryTypeLabel(row.queryType)}
											</Badge>
										</td>
										<td className="citation-data-grid__td">
											<div className="citation-data-grid__prompt">
												<ProviderIcon provider={row.provider} size={18} />
												<span>{row.providerName}</span>
											</div>
										</td>
										<td className="citation-data-grid__td citation-data-grid__td--response">
											<p className="citation-data-grid__response-text">
												{row.response_snippet}
											</p>
										</td>
										<td className="citation-data-grid__td citation-data-grid__td--center">
											<div className="citation-data-grid__status-stack">
												{row.cited ? (
													<Badge variant="success">Cited</Badge>
												) : null}
												{row.mentioned ? (
													<Badge variant="info">Mentioned</Badge>
												) : null}
												{!row.cited && !row.mentioned ? (
													<span className="confidence--none">—</span>
												) : null}
											</div>
										</td>
										<td className="citation-data-grid__td citation-data-grid__td--center">
											<span
												className={`capitalize ${CONFIDENCE_CLASS[row.confidence] || CONFIDENCE_CLASS.not_determined}`}
											>
												{row.confidence === "not_determined" ? "-" : row.confidence}
											</span>
										</td>
										<td className="citation-data-grid__td citation-data-grid__td--center">
											<span className={row.domain_mentioned ? "check--yes" : "check--no"}>
												{row.domain_mentioned ? "✓" : "✗"}
											</span>
										</td>
										<td className="citation-data-grid__td citation-data-grid__td--center">
											<span className={row.brand_mentioned ? "check--yes" : "check--no"}>
												{row.brand_mentioned ? "✓" : "✗"}
											</span>
										</td>
										<td className="citation-data-grid__td citation-data-grid__td--center">
											{row.competitor_brands?.length > 0 ? (
												<button
													type="button"
													className="citation-data-grid__link-btn"
													onClick={(e) => {
														e.stopPropagation();
														setSelectedRowDetail(row);
													}}
												>
													<Users className="citation-data-grid__link-icon" />
													{row.competitor_brands.length}
												</button>
											) : (
												<div
													className="citation-data-grid__link-btn"
													style={{ cursor: "default", color: "#9ca3af" }}
												>
													<Users className="citation-data-grid__link-icon" />
													0
												</div>
											)}
										</td>
										<td className="citation-data-grid__td citation-data-grid__td--center">
											{getRowSourceCount(row) > 0 ? (
												<button
													type="button"
													className="citation-data-grid__link-btn"
													onClick={(e) => {
														e.stopPropagation();
														setSelectedRowDetail(row);
													}}
												>
													<Link2 className="citation-data-grid__link-icon" />
													{getRowSourceCount(row)}
												</button>
											) : (
												<div
													className="citation-data-grid__link-btn"
													style={{ cursor: "default", color: "#9ca3af" }}
												>
													<Link2 className="citation-data-grid__link-icon" />
													0
												</div>
											)}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				)}
			</div>

			{selectedCompareQuery && (
				<CitationCompareModal
					queryResult={selectedCompareQuery}
					onClose={() => setSelectedCompareQuery(null)}
				/>
			)}

			{!compareMode && selectedRowDetail && (
				<div
					className="grader-dialog"
					onClick={() => setSelectedRowDetail(null)}
					role="presentation"
				>
					<div
						className="grader-dialog__content"
						onClick={(e) => e.stopPropagation()}
						role="dialog"
						aria-modal="true"
					>
						<div className="grader-dialog__header">
							<h3 className="grader-dialog__title">
								<ProviderIcon provider={selectedRowDetail.provider} size={20} />
								{selectedRowDetail.providerName} Response Details
							</h3>
							<button
								type="button"
								className="grader-dialog__close"
								onClick={() => setSelectedRowDetail(null)}
								aria-label="Close"
							>
								×
							</button>
						</div>

						<div className="grader-dialog__section">
							<h4 className="grader-dialog__section-title">Query</h4>
							<div className="grader-dialog__box">
								<p className="report-section__callout-text">{selectedRowDetail.query}</p>
							</div>
						</div>

						{(selectedRowDetail.full_response_snippet ||
							selectedRowDetail.response_snippet) && (
							<div className="grader-dialog__section">
								<h4 className="grader-dialog__section-title">AI Response</h4>
								<div className="grader-dialog__box grader-dialog__box--scroll">
									<div className="grader-dialog__markdown">
										<ReactMarkdown components={markdownComponents}>
											{preprocessResponse(
												selectedRowDetail.full_response_snippet ||
													selectedRowDetail.response_snippet
											)}
										</ReactMarkdown>
									</div>
								</div>
							</div>
						)}

						<div className="grader-dialog__section">
							<h4 className="grader-dialog__section-title">Brands in this response</h4>
							{selectedRowDetail.competitor_brands?.length > 0 ? (
								<div className="grader-dialog__brand-list">
									{selectedRowDetail.competitor_brands.map((brand) => (
										<Badge key={brand} variant="info">
											{brand}
										</Badge>
									))}
								</div>
							) : (
								<p className="report-section__summary-text">No brands detected</p>
							)}
						</div>

						<div className="grader-dialog__section">
							<h4 className="grader-dialog__section-title">Sources cited</h4>
							{getRowSources(selectedRowDetail).length > 0 ? (
								<ol className="grader-dialog__source-list">
									{getRowSources(selectedRowDetail).map((source) => (
										<li key={source}>
											<span className="grader-dialog__grounded-source">
												{formatGroundedSource(source)}
											</span>
										</li>
									))}
								</ol>
							) : (
								<p className="report-section__summary-text">No sources cited</p>
							)}
						</div>

						<div className="grader-dialog__footer">
							<div className="citation-data-grid__prompt citation-data-grid__status-stack">
								<span>Status:</span>
								{selectedRowDetail.cited ? (
									<Badge variant="success">Cited</Badge>
								) : null}
								{selectedRowDetail.mentioned ? (
									<Badge variant="info">Mentioned</Badge>
								) : null}
								{!selectedRowDetail.cited && !selectedRowDetail.mentioned ? (
									<Badge variant="secondary">Not cited</Badge>
								) : null}
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default CitationDataGrid;
